import type { Message } from "@langchain/langgraph-sdk";

import type { AskDecodaResponseBlock } from "@/types/chat-thread";

/** Subscription-local token accounting, not another transcript or event log. */
export interface ResponseBlockBarrier {
  blocks: readonly AskDecodaResponseBlock[];
  sdkTextOffsets: ReadonlyMap<string, number>;
}

export function readStreamedBlockTexts(
  messages: readonly Message[],
): Map<string, string> {
  const texts = new Map<string, string>();
  for (const message of messages) {
    if (
      message.type === "ai" &&
      message.id &&
      typeof message.content === "string"
    ) {
      texts.set(message.id, message.content);
    }
  }
  return texts;
}

/** Capture the SDK's live getter in the snapshot callback, before React effects. */
export function captureResponseBlockBarrier(
  blocks: readonly AskDecodaResponseBlock[],
  messages: readonly Message[],
): ResponseBlockBarrier {
  const texts = readStreamedBlockTexts(messages);
  return {
    blocks,
    // Only committed segments consume a prefix. A checkpoint can arrive while
    // another segment is provisional; its tokens must remain visible in full.
    sdkTextOffsets: new Map(
      blocks.flatMap((block) =>
        block.type === "text"
          ? [[block.id, texts.get(block.id)?.length ?? 0] as const]
          : [],
      ),
    ),
  };
}

/** A canonical advance supersedes uncommitted alternatives at that boundary. */
export function retainUncommittedBlockPlacements(
  blocks: readonly AskDecodaResponseBlock[],
  placements: ReadonlyMap<string, string | null>,
): Map<string, string | null> {
  const ids = new Set(blocks.map((block) => block.id));
  return new Map(
    [...placements].filter(([id, afterId]) => {
      if (ids.has(id)) return false;
      if (afterId === null) return blocks.length === 0;
      const anchor = blocks.findIndex((block) => block.id === afterId);
      return anchor === -1 || anchor === blocks.length - 1;
    }),
  );
}

/** Overlay SDK suffixes at server-specified positions; never infer chronology. */
export function reconcileResponseBlocks(
  barrier: ResponseBlockBarrier,
  messages: readonly Message[],
  placements: ReadonlyMap<string, string | null>,
): AskDecodaResponseBlock[] {
  const texts = readStreamedBlockTexts(messages);
  const blocks: AskDecodaResponseBlock[] = barrier.blocks.map((block) => {
    if (block.type !== "text") return block;
    const suffix = (texts.get(block.id) ?? "").slice(
      barrier.sdkTextOffsets.get(block.id) ?? 0,
    );
    return suffix ? { ...block, text: block.text + suffix } : block;
  });
  const present = new Set(blocks.map((block) => block.id));
  const pending = [...placements].filter(
    ([id]) => !present.has(id) && Boolean(texts.get(id)),
  );
  // Resolve chains without relying on custom-event arrival order. A missing
  // anchor remains buffered until a canonical snapshot supplies its position.
  while (pending.length > 0) {
    let inserted = false;
    const siblings = new Map<string | null, number>();
    for (const [, anchor] of pending) {
      siblings.set(anchor, (siblings.get(anchor) ?? 0) + 1);
    }
    for (let index = 0; index < pending.length; ) {
      const [id, afterId] = pending[index];
      const anchor =
        afterId === null ? -1 : blocks.findIndex((b) => b.id === afterId);
      if (
        (afterId !== null && anchor === -1) ||
        (siblings.get(afterId) ?? 0) > 1
      ) {
        index += 1;
        continue;
      }
      blocks.splice(anchor + 1, 0, { type: "text", id, text: texts.get(id)! });
      present.add(id);
      pending.splice(index, 1);
      inserted = true;
    }
    if (!inserted) break;
  }
  return blocks;
}

export function responseBlocksText(
  blocks: readonly AskDecodaResponseBlock[],
): string {
  return blocks
    .flatMap((block) => (block.type === "text" ? [block.text] : []))
    .join("");
}
